import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { type ReactNode, useRef, useState } from "react";
import { rootDomain } from "../../../shared/connectors/urlDomain";
import type { CustomDomainSetup } from "../../../shared/generatedDrizzleSchema";
import {
  CUSTOM_DOMAIN_SETUP_EXPIRY_MS,
  customDomainHostname,
  customDomainSchema,
  DOMAIN_MAX_LENGTH,
} from "../../../shared/customDomains";
import { sharedMutators } from "../../../shared/mutators";
import { useNow } from "../../hooks/useNow";
import { useSpaces } from "../../hooks/useSpaces";
import { classifyZeroResult } from "../../lib/zeroResultError";
import { ModalField, SectionHeading } from "../AiProviderModal/modalForm";
import { Box } from "../Box";
import { Button } from "../Button";
import { InstructionsStep } from "../ConnectorModal/InstructionsStep";
import { Input } from "../Input";
import { Modal } from "../Modal";
import { DangerSection } from "../Settings/DangerSection";
import { GuideStep, GuideToggle } from "../Settings/Guide";
import { RouteSpacePicker } from "../Settings/NetworksSettings/RouteSpacePicker";
import { Card, NeutralIconBadge, settingsModalStyle } from "../Settings/shared";
import { SettingsRow } from "../SettingsCard";
import {
  HeaderRight,
  SettingsModalBody,
  SettingsModalFooter,
  SettingsModalHeader,
} from "../SettingsLayout";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import { Banner } from "../Banner";
import { DnsRecordList } from "./DomainDnsRecords";
import { DomainSetupProgress } from "./DomainSetupProgress";
import { attentionMessage, settingUp } from "./status";

export type DomainChange = {
  enabled?: boolean;
  setAsDefault?: boolean;
};

type Props = {
  setup?: CustomDomainSetup;
  onClose: () => void;
  onCreated: (hostname: string) => void;
  onChange: (setup: CustomDomainSetup, change: DomainChange) => void;
  onDelete: (setup: CustomDomainSetup) => void;
};

function timeLeft(ms: number) {
  const hours = Math.floor(ms / (60 * 60_000));
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days} ${days === 1 ? "day" : "days"}`;
  if (hours > 0) return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  return "less than an hour";
}

function dnsGuide(now: number, setup?: CustomDomainSetup) {
  if (!setup) return "Next, you’ll add three records at your DNS provider.";
  const zone = rootDomain(setup.domain);
  const expiry = settingUp(setup)
    ? ` You have ${timeLeft(setup.updatedAt + CUSTOM_DOMAIN_SETUP_EXPIRY_MS - now)} to finish.`
    : "";
  return `Add these records wherever you manage DNS for \`${zone}\` and keep them in place.${expiry}`;
}

const SectionBody = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

function Section({
  showGuide,
  title,
  label,
  guide,
  children,
}: {
  showGuide: boolean;
  title: string;
  label?: string;
  guide: string;
  children?: ReactNode;
}) {
  if (showGuide)
    return (
      <GuideStep title={title} guide={guide} showGuide>
        {children}
      </GuideStep>
    );
  return (
    <InstructionsStep>
      {label ? (
        <ModalField label={label}>
          <SectionBody>{children}</SectionBody>
        </ModalField>
      ) : (
        children
      )}
    </InstructionsStep>
  );
}

function parseDomain(input: string, spaceId: string | null) {
  const maxLength = DOMAIN_MAX_LENGTH - (spaceId ? 0 : 2);
  return customDomainSchema
    .max(maxLength, `Use ${maxLength} characters or fewer.`)
    .safeParse(
      input
        .trim()
        .replace(/^[a-z][a-z0-9+.-]*:\/\//i, "")
        .replace(/[/?#].*$/, "")
        .replace(/:\d+$/, "")
        .replace(/\.$/, "")
    );
}

export function CustomDomainModal({
  setup,
  onClose,
  onCreated,
  onChange,
  onDelete,
}: Props) {
  const z = useZero();
  const inputRef = useRef<HTMLInputElement>(null);
  const [id] = useState(() => nanoid(12));
  const [domain, setDomain] = useState("");
  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string>();
  const [showGuide, setShowGuide] = useState(
    () => !setup || !(setup.enabled && setup.status === "ready")
  );
  const [spaces] = useSpaces({ settingsView: true });
  const now = useNow(60_000);
  const current = setup ?? { domain, spaceId };
  const parsed = parseDomain(domain, spaceId);
  const inputError =
    serverError ??
    (touched && domain.trim() && !parsed.success
      ? parsed.error.issues[0]!.message
      : undefined);
  const previewDomain = setup?.domain ?? (parsed.success ? parsed.data : null);
  const space = spaces.find((space) => space.id === current.spaceId);
  async function add() {
    if (!parsed.success) return;
    setPending(true);
    const result = await z.mutate(
      sharedMutators.addCustomDomain({ id, domain: parsed.data, spaceId })
    ).server;
    if (result.type === "success")
      onCreated(customDomainHostname(parsed.data, spaceId));
    else {
      const decision = classifyZeroResult(result);
      setServerError(
        decision.kind === "toast" && decision.message
          ? decision.message
          : "We couldn’t add this domain. Try again."
      );
      setPending(false);
    }
  }
  return (
    <Modal
      open
      onClose={onClose}
      aria-label="Custom domain"
      style={settingsModalStyle}
      initialFocusRef={inputRef}
    >
      <SettingsModalHeader>
        <NeutralIconBadge iconName="globe" />
        <Text variant="text-regular-md" ellipsis>
          {setup?.hostname ?? "Add custom domain"}
        </Text>
        <HeaderRight style={{ gap: 6 }}>
          <GuideToggle
            isOn={showGuide}
            onToggle={() => setShowGuide((was) => !was)}
          />
          <Button
            size="l"
            variant="secondary"
            icon="x"
            aria-label="Close"
            onClick={onClose}
          />
        </HeaderRight>
      </SettingsModalHeader>
      <SettingsModalBody style={{ gap: 16 }}>
        <Section
          title="Step 1: Choose a domain"
          guide="Enter a domain you own to serve your workflow pages and webhooks from."
          showGuide={showGuide}
        >
          <ModalField
            label="Domain"
            description={
              setup && !setup.managed
                ? "This domain is already configured. Keep its existing DNS records in place."
                : undefined
            }
          >
            <Input
              ref={inputRef}
              aria-label="Domain"
              value={current.domain}
              onChange={(e) => {
                if (pending) return;
                setDomain(e.target.value);
                setServerError(undefined);
              }}
              onBlur={() => setTouched(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !pending) {
                  setTouched(true);
                  void add();
                }
              }}
              disabled={!!setup}
              placeholder="workflows.example.com"
              autoCapitalize="none"
              autoComplete="off"
              spellCheck={false}
              style={{ width: "100%" }}
            />
            {inputError && (
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-danger)">
                  {inputError}
                </Text>
              </Box>
            )}
          </ModalField>
          <ModalField
            label="Use for"
            description={
              current.spaceId
                ? `Serves ${space?.name ?? "this space"}’s workflows at ${previewDomain ?? "this domain"}.`
                : "Serves each space’s workflows at its own subdomain."
            }
          >
            <RouteSpacePicker
              value={current.spaceId}
              onChange={(next) => {
                if (!pending) setSpaceId(next);
              }}
              disabled={!!setup}
              allSpacesLabel="Entire tenant"
              excludePersonal
              groupSpaces
            />
          </ModalField>
        </Section>
        {(!setup || setup.managed) && (showGuide || setup) && (
          <Section
            title="Step 2: Add DNS records"
            label="DNS records"
            guide={dnsGuide(now, setup)}
            showGuide={showGuide}
          >
            {setup && attentionMessage(setup) && (
              <Banner>{attentionMessage(setup)}</Banner>
            )}
            {setup && <DnsRecordList setup={setup} />}
          </Section>
        )}
        {setup?.managed && (
          <Section
            title="Step 3: Finish setup"
            label="HTTPS"
            guide="Once your records are detected, we’ll issue an HTTPS certificate for your domain. This usually takes a few minutes."
            showGuide={showGuide}
          >
            <DomainSetupProgress setup={setup} />
          </Section>
        )}
        {setup && (
          <Card>
            <SettingsRow>
              <SectionHeading
                title={
                  showGuide
                    ? `Step ${setup.managed ? 4 : 2}: Enable domain`
                    : "Enable domain"
                }
                description={
                  setup.enabled || setup.status === "ready"
                    ? "Use this domain for workflow URLs. Set it as the default in the domains list to use it for new links."
                    : "Available once setup is complete."
                }
              />
              <Toggle
                isOn={!!setup.enabled}
                disabled={!setup.enabled && setup.status !== "ready"}
                onToggle={() => onChange(setup, { enabled: !setup.enabled })}
                ariaLabel="Enable domain"
              />
            </SettingsRow>
          </Card>
        )}
        {setup && (
          <DangerSection
            title="Delete domain"
            description="Removes this domain. This can’t be undone."
            actionLabel="Delete domain"
            onAction={() => onDelete(setup)}
          />
        )}
      </SettingsModalBody>
      <SettingsModalFooter style={{ gap: 8 }}>
        {!setup && (
          <Button size="l" variant="subtle" onClick={onClose}>
            Cancel
          </Button>
        )}
        <Button
          size="l"
          disabled={!setup && (!parsed.success || pending)}
          onClick={setup ? onClose : add}
        >
          {setup ? "Done" : "Continue"}
        </Button>
      </SettingsModalFooter>
    </Modal>
  );
}
